import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import { ReconciliationStatusFilter, ReconciliationSuppliersList } from './types';

const endpoint = 'ums/v1/cms/reconciliation/suppliers';

interface QueryParams {
  month: string;
  search?: string;
  status?: ReconciliationStatusFilter;
  page: number;
  limit: number;
}

type TData = ReconciliationSuppliersList;
type TError = AxiosError;
type TQueryKey = ['reconciliation-suppliers', string, QueryParams];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetReconciliationSuppliersApi(month: string, options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const { getPageParams } = usePageParams();
  const { search, status } = getPageParams();

  const params: QueryParams = {
    month,
    search: search ? String(search) : undefined,
    status: status ? (String(status) as ReconciliationStatusFilter) : undefined,
    page,
    limit,
  };

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, { params });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(['reconciliation-suppliers', month, params], queryFn, {
    enabled: isLoggedIn && !!month,
    ...options,
  });
}
